import { createContext, useCallback, useContext, type ReactNode } from 'react'
import {
  RadarSignalsProvider,
  useRadarToken,
} from '@workos/radar-signals/react'

const SignalsContext = createContext<(() => Promise<string>) | null>(null)

function SignalsBridge({ children }: { children: ReactNode }) {
  const { getToken } = useRadarToken()
  return (
    <SignalsContext.Provider value={getToken}>
      {children}
    </SignalsContext.Provider>
  )
}

export function RadarSignals({ children }: { children: ReactNode }) {
  const clientId = import.meta.env.VITE_WORKOS_CLIENT_ID
  if (!clientId) {
    return (
      <SignalsContext.Provider value={null}>{children}</SignalsContext.Provider>
    )
  }
  return (
    <RadarSignalsProvider clientId={clientId}>
      <SignalsBridge>{children}</SignalsBridge>
    </RadarSignalsProvider>
  )
}

export function useSignalsId(): () => Promise<string | undefined> {
  const getToken = useContext(SignalsContext)
  return useCallback(async () => {
    try {
      // Compatibility with @workos/radar-signals 0.0.1: child effects (SSO's
      // immediate exchange) run before the provider sets its initialization
      // promise. Defer the read or a cold load returns "" at once. Revalidate
      // this ordering on upgrades with radarSignals.mount.test.tsx and a
      // cold browser SSO callback; see docs/enterprise-sso.md, Radar monitoring.
      await Promise.resolve()
      return (await getToken?.()) || undefined
    } catch {
      return undefined
    }
  }, [getToken])
}
